import type { ReactNode } from 'react';
import { Button, Tag } from '@viquard/doc-ui/components';
import type { CalendarEvent, CalendarKindMap } from '../types.js';
import { resolveKind } from '../utils/kindTheme.js';
import { eventInstanceKey } from '../utils/identity.js';

export interface AllDayStripProps {
  readonly events: readonly CalendarEvent[];
  readonly kindTheme: CalendarKindMap;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly selectedEventKey?: string | null | undefined;
  /** Optional ARIA label override. */
  readonly label?: string | undefined;
}

/**
 * Compact strip rendered above the timed grid in Week / Day views to
 * surface all-day events. Each item is a doc-ui `Button` (interactive)
 * or `Tag` (static); the per-event kind tint is exposed via a leading
 * dot icon, which keeps the list 100% on doc-ui primitives.
 */
export function AllDayStrip(props: AllDayStripProps): ReactNode {
  const { events, kindTheme, onSelectEvent, selectedEventKey, label = 'All-day events' } = props;
  if (events.length === 0) return null;
  const interactive = onSelectEvent !== undefined;
  return (
    <div className="vq-cal__allday" role="list" aria-label={label}>
      <span className="vq-cal__allday-label" aria-hidden="true">
        All-day
      </span>
      <div className="vq-cal__allday-items">
        {events.map((e) => {
          const key = eventInstanceKey(e);
          const kind = resolveKind(kindTheme, e.kind);
          const selected = selectedEventKey === key;
          const dot = (
            <span
              className="vq-cal__allday-kind-dot"
              style={{ background: kind.color }}
              aria-hidden="true"
            />
          );
          if (interactive) {
            return (
              <Button
                key={key}
                variant="secondary"
                size="sm"
                shape="pill"
                {...(selected ? { className: 'is-selected' } : {})}
                iconLeft={dot}
                role="listitem"
                aria-pressed={selected}
                onClick={() => { onSelectEvent(e); }}
                title={e.title}
              >
                {e.title}
              </Button>
            );
          }
          return (
            <Tag
              key={key}
              tone="neutral"
              size="sm"
              role="listitem"
              {...(selected ? { className: 'is-selected' } : {})}
            >
              <span className="vq-cal__allday-tag-inner">
                {dot}
                <span>{e.title}</span>
              </span>
            </Tag>
          );
        })}
      </div>
    </div>
  );
}
